import { useEffect, useRef, useState } from 'react';
import type { ActionId, IsoDate, ObjectiveId, ObjectiveSummaryView, ObjectiveView, PriorityView } from '@contracts';
import { useDomainApi } from '@ui/shared/domain/DomainApiContext';
import { CheckControl } from '@ui/shared/primitives/CheckControl';
import { Plus, Trash2, ChevronDown } from '@ui/shared/icons';
import { objectiveCategoryTokenPrefix } from '@ui/desktop/objective-form/FormPrimitives';
import { PickerOptionRow } from '@ui/desktop/objective-form/FormPrimitives';
import { CardTitle, EmptyCaption, ObjectiveAccentChip, TodayCard, todayInputStyle } from './primitives';

const PRIORITY_CAP = 5;

/**
 * Row C (main) — Priorities (R8.2, design/screens/today-desktop-v2.md SS9.1).
 * Checkbox + title + objective-link chip OR neutral "Standalone" label +
 * optional time badge + hover-reveal icon affordances. Cap-reached renders a
 * friendly explanation, never an error treatment (AC-T4).
 */
export function PrioritiesCard({
  date,
  priorities,
  objectives,
  onChanged,
  onOpenObjective,
}: {
  readonly date: IsoDate;
  readonly priorities: readonly PriorityView[];
  readonly objectives: readonly ObjectiveSummaryView[];
  readonly onChanged: () => void;
  readonly onOpenObjective: (objectiveId: string) => void;
}) {
  const api = useDomainApi();
  const [composerOpen, setComposerOpen] = useState(false);
  // Bug fix (qa-engineer sweep, AC-T6 adversarial edge case): a linked
  // Priority whose underlying action was later removed (from the Objective
  // screen, independently of this card) still renders normally here, but
  // completing it throws `action_not_found` — `completePriority` calls
  // through to the same `completeAction` path the ADR's reconciliation
  // design relies on, and that path requires the action to still exist.
  // This wasn't a violation of "never able to disagree" (there's genuinely
  // one fact in the log), just an unhandled failure mode the design didn't
  // address. Tracked per-row (not global) so one orphaned Priority doesn't
  // block interacting with the rest of the list; the only recovery offered
  // is Remove, since that path never depends on the linked action existing.
  const [orphanedId, setOrphanedId] = useState<string | null>(null);
  const doneCount = priorities.filter((p) => p.completed).length;
  const capReached = priorities.length >= PRIORITY_CAP;

  function toggle(priority: PriorityView) {
    setOrphanedId(null);
    if (priority.completed) {
      if (priority.entryEventId) {
        void api.revertLogEntry(priority.entryEventId).then(onChanged);
      }
      return;
    }
    void api
      .completePriority(priority.priorityId, date)
      .then(onChanged)
      .catch(() => setOrphanedId(priority.priorityId));
  }

  function remove(priority: PriorityView) {
    void api.removePriority(priority.priorityId).then(onChanged);
  }

  return (
    <TodayCard label="Priorities">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-3)' }}>
        <CardTitle>Priorities</CardTitle>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
          <span className="num" style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)' }}>
            {doneCount} of {PRIORITY_CAP} done
          </span>
          <button
            type="button"
            onClick={() => setComposerOpen(true)}
            style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', minHeight: 'var(--touch-target-min)', color: 'var(--hue-primary)', fontWeight: 600, fontSize: 'var(--type-caption-size)' }}
          >
            <Plus size={14} aria-hidden="true" />
            Add
          </button>
        </div>
      </div>

      <div style={{ marginTop: 'var(--space-3)' }}>
        {priorities.length === 0 && !composerOpen ? (
          <EmptyCaption>Nothing pinned as a priority yet today.</EmptyCaption>
        ) : (
          priorities.map((priority, i) => (
            <PriorityRow
              key={priority.priorityId}
              priority={priority}
              isLast={i === priorities.length - 1}
              orphaned={priority.priorityId === orphanedId}
              onToggle={() => toggle(priority)}
              onRemove={() => remove(priority)}
              onOpenObjective={onOpenObjective}
            />
          ))
        )}

        {composerOpen && (
          <PriorityComposer
            date={date}
            objectives={objectives}
            capReached={capReached}
            onClose={() => setComposerOpen(false)}
            onCreated={() => {
              setComposerOpen(false);
              onChanged();
            }}
          />
        )}
      </div>
    </TodayCard>
  );
}

function PriorityRow({
  priority,
  isLast,
  orphaned,
  onToggle,
  onRemove,
  onOpenObjective,
}: {
  readonly priority: PriorityView;
  readonly isLast: boolean;
  readonly orphaned: boolean;
  readonly onToggle: () => void;
  readonly onRemove: () => void;
  readonly onOpenObjective: (objectiveId: string) => void;
}) {
  const [hover, setHover] = useState(false);

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        padding: 'var(--space-3) 0',
        borderTop: '1px solid var(--border-subtle)',
        borderBottom: isLast ? '1px solid var(--border-subtle)' : 'none',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
        <CheckControl
          checked={priority.completed}
          onToggle={onToggle}
          label={priority.completed ? `${priority.title}, completed. Tap to undo.` : `Mark ${priority.title} complete`}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 'var(--type-body-size)',
              fontWeight: 500,
              color: priority.completed ? 'var(--text-muted)' : 'var(--text-primary)',
            }}
          >
            {priority.title}
          </div>
          <div style={{ marginTop: 'var(--space-1)' }}>
            {priority.linkedAction ? (
              <ObjectiveAccentChip
                title={priority.linkedAction.objectiveTitle}
                tokenPrefix={objectiveCategoryTokenPrefix(priority.linkedAction.objectiveCategory)}
                onSelect={() => onOpenObjective(priority.linkedAction!.objectiveId)}
              />
            ) : (
              <span style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)' }}>Standalone</span>
            )}
          </div>
        </div>
        {priority.timeLabel && (
          <span className="num" style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)', flex: 'none' }}>
            {priority.timeLabel}
          </span>
        )}
        <div
          style={{
            display: 'flex',
            gap: 'var(--space-1)',
            flex: 'none',
            opacity: hover ? 1 : 0,
            transition: 'opacity var(--dur-fast) var(--ease-standard)',
          }}
        >
          {/* Bug fix (found independently by both the ux-designer audit and
              the qa-engineer sweep): this row previously showed a SECOND
              hover button — a Pencil (edit) glyph, aria-labeled "Remove", and
              wired to the delete handler — plus a "Notes" MessageSquare
              button with no onClick at all. Neither an edit affordance nor a
              per-Priority notes field exists anywhere in PriorityView/the
              ADR, so "Notes" referenced a feature that was never built, not
              a wiring oversight. Reduced to the one real, correctly-labeled
              action: remove, using the same Trash2 icon every other
              destructive-but-non-punitive control in the app already uses
              (Brain dump's delete, ActionsTable's per-action delete) — never
              styled as a warning/red, matching the standing tone rule. */}
          <button
            type="button"
            aria-label={`Remove ${priority.title}`}
            onClick={onRemove}
            style={{ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}
          >
            <Trash2 size={16} aria-hidden="true" />
          </button>
        </div>
      </div>
      {/* Bug fix (qa-engineer sweep, AC-T6): a linked Priority whose
          underlying action was removed elsewhere throws when completed —
          surfaced here as a calm, factual status line (never a red/alert
          treatment) with the one recovery path that's always safe. */}
      {orphaned && (
        <p role="status" style={{ margin: 'var(--space-2) 0 0', paddingLeft: 'calc(var(--touch-target-min) + var(--space-3))', fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)' }}>
          Its linked action was removed, so this can&apos;t be completed anymore — remove it, or leave it as a record.
        </p>
      )}
    </div>
  );
}

function PriorityComposer({
  date,
  objectives,
  capReached,
  onClose,
  onCreated,
}: {
  readonly date: IsoDate;
  readonly objectives: readonly ObjectiveSummaryView[];
  readonly capReached: boolean;
  readonly onClose: () => void;
  readonly onCreated: () => void;
}) {
  const api = useDomainApi();
  const [title, setTitle] = useState('');
  const [linkedAction, setLinkedAction] = useState<{ readonly objectiveId: ObjectiveId; readonly actionId: ActionId } | null>(null);
  const [linkedLabel, setLinkedLabel] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  if (capReached) {
    return (
      <div style={{ padding: 'var(--space-3) 0', borderTop: '1px solid var(--border-subtle)' }}>
        <p style={{ margin: 0, fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)' }}>
          Five&apos;s the limit for today — finish or clear one first to add another.
        </p>
        <button type="button" onClick={onClose} style={{ marginTop: 'var(--space-2)', fontSize: 'var(--type-caption-size)', fontWeight: 600, color: 'var(--hue-primary)' }}>
          Close
        </button>
      </div>
    );
  }

  async function submit() {
    if (title.trim().length === 0) return;
    await api.createPriority(date, { title: title.trim(), linkedAction, timeLabel: null });
    onCreated();
  }

  return (
    <div style={{ padding: 'var(--space-3) 0', borderTop: '1px solid var(--border-subtle)', display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
      <input
        autoFocus
        aria-label="New Priority title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') void submit();
          if (e.key === 'Escape') onClose();
        }}
        placeholder="What's the priority?"
        style={todayInputStyle}
      />
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)', minHeight: 'var(--touch-target-min)' }}
        >
          {linkedLabel ?? 'Standalone'}
          <ChevronDown size={14} aria-hidden="true" />
        </button>
        {pickerOpen && (
          <ObjectiveActionPickerPopover
            objectives={objectives}
            onSelect={(objectiveId, actionId, label) => {
              setLinkedAction({ objectiveId, actionId });
              setLinkedLabel(label);
              setPickerOpen(false);
            }}
            onClearLink={() => {
              setLinkedAction(null);
              setLinkedLabel(null);
              setPickerOpen(false);
            }}
            onClose={() => setPickerOpen(false)}
          />
        )}
        <div style={{ flex: 1 }} />
        <button type="button" onClick={onClose} style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)', minHeight: 'var(--touch-target-min)' }}>
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void submit()}
          style={{
            minHeight: 'var(--touch-target-min)',
            padding: '0 var(--space-3)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--hue-primary-vivid)',
            color: 'var(--text-on-accent)',
            fontWeight: 600,
            fontSize: 'var(--type-caption-size)',
          }}
        >
          Add
        </button>
      </div>
    </div>
  );
}

/** Objective+action picker for linking a Priority — two-step (choose
 *  objective, then its action), reusing `ObjectivePickerPopover`'s option-row
 *  interaction shape, scaled to pick a specific action (spec SS9.1). */
function ObjectiveActionPickerPopover({
  objectives,
  onSelect,
  onClearLink,
  onClose,
}: {
  readonly objectives: readonly ObjectiveSummaryView[];
  readonly onSelect: (objectiveId: ObjectiveId, actionId: ActionId, label: string) => void;
  readonly onClearLink: () => void;
  readonly onClose: () => void;
}) {
  const api = useDomainApi();
  const [chosenObjective, setChosenObjective] = useState<ObjectiveView | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handlePointerDown(e: MouseEvent) {
      if (ref.current?.contains(e.target as Node)) return;
      onClose();
    }
    const id = window.setTimeout(() => document.addEventListener('mousedown', handlePointerDown), 0);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      role="listbox"
      aria-label="Link to an objective action"
      style={{
        position: 'absolute',
        zIndex: 41,
        top: 'calc(100% + var(--space-2))',
        left: 0,
        background: 'var(--surface-overlay)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-2)',
        border: '1px solid var(--border-subtle)',
        padding: 'var(--space-2)',
        minWidth: '260px',
        maxHeight: '260px',
        overflowY: 'auto',
      }}
    >
      {!chosenObjective ? (
        <>
          <PickerOptionRow label="Standalone" selected={false} onSelect={onClearLink} />
          {objectives.map((o) => (
            <PickerOptionRow
              key={o.objectiveId}
              label={o.title}
              selected={false}
              onSelect={() => void api.getObjective(o.objectiveId).then(setChosenObjective)}
            />
          ))}
        </>
      ) : chosenObjective.actions.filter((a) => !a.completed).length === 0 ? (
        <p style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)', padding: '0 var(--space-2)' }}>
          No open actions on this objective.
        </p>
      ) : (
        chosenObjective.actions
          .filter((a) => !a.completed)
          .map((a) => (
            <PickerOptionRow
              key={a.actionId}
              label={a.title}
              selected={false}
              onSelect={() => onSelect(chosenObjective.objectiveId, a.actionId, `${chosenObjective.title} · ${a.title}`)}
            />
          ))
      )}
    </div>
  );
}
